import type { SortPayload } from 'react-admin';
import { downloadCSV } from 'react-admin';
import type { CategoryResource } from '../../../Entities/Category';
import type { ClickResource } from '../../../Entities/Click';
import type { MerchantResource } from '../../../Entities/Merchant';
import type { OfferResource } from '../../../Entities/Offer';
import type { ProductResource } from '../../../Entities/Product';
import { escapeCsv } from '../../../Components/ReportsWidget/utils';
import type { GPDataProvider } from '../../../DataProvider';
import { idFrom } from '../../../utils';

const EXPORT_PAGE_SIZE = 2000;
const EXPORT_PREFETCH = ['category', 'merchant', 'offer', 'product'];
const EXPORT_FIELDS = {
  categories: ['name'],
  merchants: ['merchant_name'],
  offers: ['name'],
  products: ['name'],
};

export interface ClickExportRequest {
  filter: Record<string, unknown>;
  sort?: SortPayload;
}

export interface ClickExportProgress {
  fetched: number;
  phase: 'fetching' | 'resolving';
  total: number;
}

interface PrefetchedData {
  categories: Map<string, CategoryResource>;
  merchants: Map<string, MerchantResource>;
  offers: Map<string, OfferResource>;
  products: Map<string, ProductResource>;
}

interface FetchedPrefetchedData {
  categories?: CategoryResource[];
  merchants?: MerchantResource[];
  offers?: OfferResource[];
  products?: ProductResource[];
}

function buildClicksCsv(records: ClickResource[], prefetched: PrefetchedData): string {
  const header = [
    'id',
    'timestamp',
    'client_ip',
    'earnings_aud_cents',
    'enhanced_partner_id',
    'is_over_budget_click',
    'refname',
    'referrer',
    'request_url',
    'site_id',
    'user_agent',
    'category_id',
    'category',
    'merchant_id',
    'merchant',
    'offer_id',
    'offer',
    'product_id',
    'product',
  ];

  const rows = records.map((record) =>
    [
      record.id,
      record.attributes.timestamp,
      record.attributes.client_ip,
      record.attributes.earnings,
      record.attributes.enhanced_partner_id,
      record.attributes.is_over_budget_click,
      record.attributes.refname,
      record.attributes.referrer,
      record.attributes.request_url,
      record.attributes.site_id,
      record.attributes.user_agent,
      idFrom(record, 'category'),
      prefetched.categories.get(idFrom(record, 'category') ?? '')?.attributes.name ?? '',
      idFrom(record, 'merchant'),
      prefetched.merchants.get(idFrom(record, 'merchant') ?? '')?.attributes.merchant_name ?? '',
      idFrom(record, 'offer'),
      prefetched.offers.get(idFrom(record, 'offer') ?? '')?.attributes.name ?? '',
      idFrom(record, 'product'),
      prefetched.products.get(idFrom(record, 'product') ?? '')?.attributes.name ?? '',
    ]
      .map(escapeCsv)
      .join(','),
  );

  return [header.join(','), ...rows].join('\n');
}

async function fetchClicksPage({
  dataProvider,
  filter,
  page,
  perPage,
  sort,
}: {
  dataProvider: GPDataProvider;
  filter: Record<string, unknown>;
  page: number;
  perPage: number;
  sort: SortPayload;
}): Promise<{
  data: ClickResource[];
  prefetched: FetchedPrefetchedData;
  total: number;
}> {
  const result = await dataProvider.getList<ClickResource>('clicks', {
    filter,
    pagination: {
      page,
      perPage,
    },
    sort,
    meta: {
      fields: EXPORT_FIELDS,
      prefetch: EXPORT_PREFETCH,
    },
  });
  const prefetched = (result.meta?.prefetched ?? {}) as FetchedPrefetchedData;

  return {
    data: result.data,
    prefetched,
    total: result.total ?? result.data.length,
  };
}

export async function exportAllClicks({
  dataProvider,
  filter,
  onProgress,
  sort,
}: ClickExportRequest & {
  dataProvider: GPDataProvider;
  onProgress: (progress: ClickExportProgress) => void;
}): Promise<number> {
  const records: ClickResource[] = [];
  const prefetched: PrefetchedData = {
    categories: new Map<string, CategoryResource>(),
    merchants: new Map<string, MerchantResource>(),
    offers: new Map<string, OfferResource>(),
    products: new Map<string, ProductResource>(),
  };
  const effectiveSort = sort ?? { field: 'id', order: 'DESC' as const };
  let page = 1;
  let total = 0;
  let fetched = 0;

  while (page === 1 || fetched < total) {
    const result = await fetchClicksPage({
      dataProvider,
      filter,
      page,
      perPage: EXPORT_PAGE_SIZE,
      sort: effectiveSort,
    });

    if (page === 1) {
      total = result.total ?? result.data.length;
    }

    records.push(...result.data);
    result.prefetched.categories?.forEach((record) => {
      prefetched.categories.set(String(record.id), record);
    });
    result.prefetched.merchants?.forEach((record) => {
      prefetched.merchants.set(String(record.id), record);
    });
    result.prefetched.offers?.forEach((record) => {
      prefetched.offers.set(String(record.id), record);
    });
    result.prefetched.products?.forEach((record) => {
      prefetched.products.set(String(record.id), record);
    });
    fetched += result.data.length;
    onProgress({ fetched, phase: 'fetching', total });

    page += 1;
  }

  onProgress({ fetched, phase: 'resolving', total });

  downloadCSV(
    buildClicksCsv(records, prefetched),
    `clicks_${new Date().toISOString().replaceAll(':', '-').slice(0, 19)}`,
  );

  return records.length;
}
